<template>
  <div
    class="message-box-wrapper"
    v-bind:class="{ 'expanded-box': expandedBox }"
  >
    <!-- XMPP file upload chips -->
    <div class="files" v-if="chatStore.state.chatSource != 'bot'">
      <div
        class="file-upload"
        v-for="(newFile, index) in newFiles"
        :key="index"
      >
        <div
          class="progress-bar"
          v-bind:class="{
            'upload-complete': newFile.uploadPercent == 100,
          }"
        >
          <div
            class="progress-bar-indicator"
            v-bind:style="{
              right: 100 - newFile.uploadPercent + '%',
              background: themeStore.theme.primaryColor,
            }"
          >
            <span
              class="bt-icon bt-icon-check-circle"
              v-bind:class="{
                'upload-complete': newFile.uploadPercent == 100,
              }"
            ></span>
          </div>
        </div>
        <div
          class="file-name"
          v-bind:class="{
            'upload-complete': newFile.uploadPercent == 100,
          }"
        >
          {{ newFile.file.name }}
        </div>
        <button
          class="upload-cancel-button"
          :aria-label="`Remove file ${newFile.file.name}`"
          @click="removeFile(newFile)"
        >
          <span class="bt-icon bt-icon-close"></span>
        </button>
      </div>
    </div>

    <!-- Bot attachment chips -->
    <div class="bot-attachments" v-if="chatStore.state.chatSource == 'bot' && botPendingAttachments.length > 0">
      <div
        class="bot-attachment-chip"
        v-for="(att, index) in botPendingAttachments"
        :key="att.id"
        :class="{
          'bot-attachment-chip--uploading': att.uploadStatus === 'uploading',
          'bot-attachment-chip--error': att.uploadStatus === 'error',
          'bot-attachment-chip--image': att.previewUrl,
        }"
      >
        <!-- Image thumbnail -->
        <a
          v-if="att.previewUrl"
          :href="att.previewUrl"
          target="_blank"
          class="bot-attachment-thumbnail-link"
          :aria-label="`Preview ${att.displayName}`"
        >
          <img :src="att.previewUrl" class="bot-attachment-thumbnail" :alt="att.displayName" />
          <div v-if="att.uploadStatus === 'uploading'" class="bot-attachment-thumbnail-overlay">
            <span class="bt-icon bt-icon-spinner bot-attachment-spinner"></span>
          </div>
          <div v-else-if="att.uploadStatus === 'error'" class="bot-attachment-thumbnail-overlay bot-attachment-thumbnail-overlay--error">
            <span class="bt-icon bt-icon-warning"></span>
          </div>
        </a>
        <!-- Non-image chip -->
        <template v-else>
          <span v-if="att.uploadStatus === 'uploading'" class="bt-icon bt-icon-spinner bot-attachment-spinner"></span>
          <span v-else-if="att.uploadStatus === 'error'" class="bt-icon bt-icon-warning bot-attachment-icon--error"></span>
          <span v-else :class="'bt-icon ' + botAttachmentIcon(att) + ' bot-attachment-icon'"></span>
          <span class="bot-attachment-name">{{ att.displayName }}</span>
        </template>
        <button
          class="bot-attachment-remove"
          :class="{ 'bot-attachment-remove--image': att.previewUrl }"
          :aria-label="`Remove attachment ${att.displayName}`"
          @click="removeBotAttachment(index)"
          type="button"
        >
          <span class="bt-icon bt-icon-close"></span>
        </button>
      </div>
    </div>

    <!-- Bot URL input row -->
    <div class="bot-url-input-row" v-if="chatStore.state.chatSource == 'bot' && botShowUrlInput">
      <input
        v-model="botPendingUrlInput"
        class="bot-url-input"
        placeholder="Paste a URL and press Enter"
        @keydown.enter.prevent="submitBotUrlInput"
        @keydown.escape="botShowUrlInput = false"
        ref="botUrlInput"
      />
      <button class="bot-url-submit" type="button" @click="submitBotUrlInput" aria-label="Add URL">
        <span class="bt-icon bt-icon-check-circle"></span>
      </button>
      <button class="bot-url-cancel" type="button" @click="botShowUrlInput = false; botPendingUrlInput = ''" aria-label="Cancel URL">
        <span class="bt-icon bt-icon-close"></span>
      </button>
    </div>

    <!-- Hidden bot file input -->
    <input
      v-if="chatStore.state.chatSource == 'bot'"
      type="file"
      class="file-input"
      ref="bot-file-input"
      :accept="botAcceptedExtensions"
      multiple
      @change="onBotFileInputChange"
    />

    <div class="message-box">
      <form
        action=""
        @submit.prevent="submitMessage"
        class="search-form"
        ref="form"
        novalidate
      >
        <textarea
          v-model="newMessageText"
          @keydown.enter.exact.prevent
          @keyup.enter.exact="submitMessage"
          @keydown.enter.shift.exact="newline"
          @input.exact="onInput"
          @keydown.exact="onKeyDown"
          @blur.exact="onBlur"
          name="message-input"
          class="message-input"
          id="message-input"
          placeholder="Type your message here"
          rows="5"
          :disabled="chatStore.state.chatSource == 'bot' && submittingMessage"
        ></textarea>
        <input
          name="file-input"
          type="file"
          id="file-input"
          class="file-input"
          ref="file-input"
          @change="onFileSelected"
        />
        <div class="input-buttons">
          <!-- XMPP attachment button -->
          <button
            type="button"
            class="message-button message-attachment-button"
            v-bind:class="{ disabled: submittingFileTransfer }"
            :disabled="submittingFileTransfer"
            aria-label="Select file to upload"
            @click="selectFile()"
            v-if="chatStore.state.chatSource != 'bot'"
          >
            <span class="bt-icon bt-icon-attachment"></span>
          </button>

          <!-- Bot attachment buttons -->
          <template v-if="chatStore.state.chatSource == 'bot'">
            <button
              type="button"
              class="message-button message-attachment-button"
              :disabled="submittingMessage"
              aria-label="Attach file or image"
              @click="$refs['bot-file-input'].click()"
            >
              <span class="bt-icon bt-icon-attachment"></span>
            </button>
            <button
              type="button"
              class="message-button message-attachment-button"
              :class="{ 'bot-url-btn--active': botShowUrlInput }"
              :disabled="submittingMessage"
              aria-label="Attach URL"
              @click="botShowUrlInput = !botShowUrlInput"
            >
              <span class="bt-icon bt-icon-link"></span>
            </button>
          </template>

          <button
            type="input"
            class="message-button message-send-button"
            v-bind:class="{
              'message-send-button-disabled': disableCTAs,
            }"
            aria-label="Send message"
            :disabled="disableCTAs"
          >
            <span class="bt-icon bt-icon-send-1" v-if="disableCTAs"></span>
            <span
              class="bt-icon bt-icon-send-filled"
              v-if="!disableCTAs"
            ></span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import chatService from '@/services/chatService'
import { uploadFile } from '@/api/commApi'
import { PasteFileHandler } from '@/utils/PasteFileHandler'
import { sendChat, sendChatStreaming, uploadFileForBot, getFileUrl, ACCEPTED_FILE_EXTENSIONS } from '../api/chatDemoApi'
import { base64ToFile } from '../utils/helpers'

export default {
  name: 'MessageBox',
  inject: ['chatStore', 'memberStore', 'themeStore', 'partnerStore'],
  data: function () {
    return {
      newMessageText: '',
      sendButtonHoverColor: this.themeStore.theme.primaryHoverColor,
      sendButtonColor: this.themeStore.theme.primaryColor,
      submittingFileTransfer: false,
      submittingMessage: false,
      uploadMessageResults: [],
      newFiles: [],
      uploadController: null,
      isTyping: false,
      typingTimer: null,
      pausedTimer: null,
      // bot attachment state
      botPendingAttachments: [],
      botShowUrlInput: false,
      botPendingUrlInput: '',
      botAcceptedExtensions: ACCEPTED_FILE_EXTENSIONS,
    }
  },
  methods: {
    newline() {},
    async submitBotMessage() {
      const msg = String(this.newMessageText).trim()
      const botMessageId = Date.now().toString()

      // Collect attachments before clearing
      const contextAttachments = this.buildBotContextAttachments()
      const displayAttachments = this.buildBotDisplayAttachments()
      
      try {
        this.submittingMessage = true
        
        // Add user message — use displayAttachments so previewUrl is available in the chat
        this.chatStore.addMessage({
          message: msg,
          name: this.memberStore.member.guestId,
          resource: this.memberStore.member.guestId,
          avatar: '/images/user-avatar.png',
          timestamp: new Date().toISOString(),
          attachments: displayAttachments.length > 0 ? displayAttachments : undefined,
        })
        
        localStorage.setItem(
          'botChat',
          JSON.stringify(this.chatStore.state.messages),
        )
        this.newMessageText = ''

        // Clear pending attachments and URL input
        this.botPendingAttachments = []
        this.botShowUrlInput = false
        this.botPendingUrlInput = ''
        
        // Show thinking indicator while waiting for stream to start
        this.chatStore.setBotThinking(true)
        this.scrollToBottom()

        // Start streaming response
        const response = await sendChatStreaming(msg, (content, traceId) => {
          // Hide thinking indicator once streaming starts
          if (this.chatStore.state.botThinking) {
            this.chatStore.setBotThinking(false)
            
            // Create placeholder bot message for streaming
            this.chatStore.addMessage({
              id: botMessageId,
              message: '',
              streamingContent: content,
              isStreaming: true,
              name: this.partnerStore.getBotDisplayName(),
              resource: 'ovation-bot',
              avatar: this.partnerStore.getBotAvatarUrl(),
              timestamp: new Date().toISOString(),
              traceId: traceId,
            })
          } else {
            // Update streaming content in real-time
            this.chatStore.updateStreamingMessage(botMessageId, content, traceId)
          }
          // Auto-scroll to bottom
          this.scrollToBottom()
        }, contextAttachments)

        // Clear thinking indicator if still showing
        this.chatStore.setBotThinking(false)
        
        // Finalize the message with complete response
        this.chatStore.finalizeStreamingMessage(botMessageId, response)
        
        localStorage.setItem(
          'botChat',
          JSON.stringify(this.chatStore.state.messages),
        )
        
        this.submittingMessage = false
        window.setTimeout(() => {
          let input = document.getElementById('message-input')
          input && input.focus()
        }, 100)
        
      } catch (error) {
        console.error('Streaming bot message error:', error)
        this.submittingMessage = false
        
        // Clear thinking indicator
        this.chatStore.setBotThinking(false)
        
        // Remove the placeholder streaming message if it was created
        this.chatStore.removeMessage(botMessageId)
        
        // Show thinking indicator for fallback API loading state
        this.chatStore.setBotThinking(true)
        
        // Fallback to regular API if streaming fails
        try {
          const response = await sendChat(msg, contextAttachments)
          
          // Clear thinking indicator
          this.chatStore.setBotThinking(false)
          
          this.chatStore.addMessage({
            message: response.message,
            name: this.partnerStore.getBotDisplayName(),
            resource: 'ovation-bot',
            avatar: this.partnerStore.getBotAvatarUrl(),
            showUserIdentification: response.showUserIdentification,
            triggers: response.triggers,
            source: response.source,
            relatedLinks: response.relatedLinks,
            timestamp: new Date().toISOString(),
          })
          localStorage.setItem(
            'botChat',
            JSON.stringify(this.chatStore.state.messages),
          )
        } catch (fallbackError) {
          console.error('Fallback API also failed:', fallbackError)
          // Clear thinking indicator on fallback failure too
          this.chatStore.setBotThinking(false)
        }
      }
    },
    scrollToBottom() {
      window.setTimeout(() => {
        const messageList = document.getElementById('message-list')
        if (messageList) {
          messageList.scrollTop = messageList.scrollHeight
        }
      }, 50)
    },
    async submitMessage() {
      if (this.chatStore.state.chatSource == 'bot') {
        if (
          (!this.newMessageText && this.botPendingAttachments.length === 0) ||
          this.submittingMessage ||
          this.hasBotUploading
        ) {
          return
        }
        return this.submitBotMessage()
      }

      if (
        (!this.newMessageText && !this.newFiles.length) ||
        this.submittingFileTransfer
      ) {
        return
      }

      // Clear typing indicator when sending message
      this.sendPausedIndicator()

      if (this.newMessageText) {
        this.newMessageText = String(this.newMessageText).trim()
        chatService.sendMessage(this.newMessageText)
        this.newMessageText = ''
      }
      if (this.newFiles.length) {
        this.newFiles.forEach(file => {
          chatService.sendMessage(JSON.stringify(file.uploadResult))
        })
        this.newFiles = []
      }
    },
    selectFile() {
      this.$refs['file-input'].click()
    },
    async onFileSelected(event) {
      const files = event.target.files
      const fileReader = new FileReader()
      if (files[0]) {
        fileReader.readAsDataURL(files[0])
        const newFile = { file: files[0], uploadPercent: 5 }
        this.newFiles.push(newFile)
        this.uploadFile(newFile)
        this.$refs['file-input'].value = ''
      }
    },
    async uploadFile(newFile) {
      // todo: any validations? file type or max file size?
      try {
        newFile.uploadController = new AbortController()

        this.submittingFileTransfer = true
        let response = await uploadFile(
          chatService.commData,
          newFile.file,
          '',
          progressEvent => {
            newFile.uploadPercent = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100),
            )
          },
          newFile.uploadController,
        )
        if (response.results && Array.isArray(response.results)) {
          newFile.uploadResult = response.results[0]
        }
        this.submittingFileTransfer = false
        this.uploadController = null

        // focus on textarea after animation finishes
        window.setTimeout(() => {
          let input = document.getElementById('message-input')
          input && input.focus()
        }, 500)
      } catch (error) {
        this.submittingFileTransfer = false
        newFile.uploadPercent = 0
        this.uploadController = null
        // todo: any sort of error notification here?
      }
    },
    removeFile(newFile) {
      // cancel pending upload
      if (newFile.uploadController) {
        newFile.uploadController.abort()
        newFile.uploadController = null
      }
      this.newFiles = this.newFiles.filter(function (file) {
        return file.file !== newFile.file
      })
    },
    // Bot attachment methods
    onBotFileInputChange(event) {
      const files = Array.from(event.target.files || [])
      files.forEach(file => this.addBotFile(file))
      event.target.value = ''
    },
    async addBotFile(file) {
      const attachmentId = Date.now() + Math.random()
      const blobUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null
      this.botPendingAttachments.push({
        id: attachmentId,
        file,
        displayName: file.name,
        uploadStatus: 'uploading',
        attachmentData: null,
        previewUrl: blobUrl,
      })
      try {
        const result = await uploadFileForBot(file)
        const att = this.botPendingAttachments.find(a => a.id === attachmentId)
        if (att) {
          att.uploadStatus = 'done'
          att.attachmentData = result
          // Keep the blob URL in the pending chip for preview; it gets cleaned up on send
        }
      } catch (err) {
        const att = this.botPendingAttachments.find(a => a.id === attachmentId)
        if (att) att.uploadStatus = 'error'
        console.error('Bot file upload failed:', err)
      }
    },
    submitBotUrlInput() {
      const url = this.botPendingUrlInput.trim()
      if (!url) return
      this.botPendingAttachments.push({
        id: Date.now() + Math.random(),
        displayName: url,
        uploadStatus: 'done',
        attachmentData: { type: 'url', url },
      })
      this.botPendingUrlInput = ''
      this.botShowUrlInput = false
    },
    removeBotAttachment(index) {
      const att = this.botPendingAttachments[index]
      // Only revoke if still a blob URL (persistent URLs must not be revoked)
      if (att?.previewUrl && att.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(att.previewUrl)
      }
      this.botPendingAttachments.splice(index, 1)
    },
    botAttachmentIcon(attachment) {
      if (!attachment.attachmentData) return 'bt-icon-attachment'
      if (attachment.attachmentData.type === 'url') return 'bt-icon-link'
      if (attachment.attachmentData.type === 'image') return 'bt-icon-image'
      return 'bt-icon-file'
    },
    buildBotContextAttachments() {
      return this.botPendingAttachments
        .filter(a => a.uploadStatus === 'done' && a.attachmentData)
        .map(a => a.attachmentData)
    },
    buildBotDisplayAttachments() {
      return this.botPendingAttachments
        .filter(a => a.uploadStatus === 'done' && a.attachmentData)
        .map(a => {
          // Revoke blob URL now that the message is being sent
          if (a.previewUrl && a.previewUrl.startsWith('blob:')) URL.revokeObjectURL(a.previewUrl)
          // No previewUrl — sent messages show filename chip only
          return { ...a.attachmentData }
        })
    },
    onInput() {
      // Only send typing indicators for XMPP chat, not bot chat
      if (this.chatStore.state.chatSource === 'bot') {
        return
      }
      
      this.sendTypingIndicator()
    },
    onKeyDown() {
      // Only send typing indicators for XMPP chat, not bot chat
      if (this.chatStore.state.chatSource === 'bot') {
        return
      }
      
      this.sendTypingIndicator()
    },
    onBlur() {
      // Only send typing indicators for XMPP chat, not bot chat
      if (this.chatStore.state.chatSource === 'bot') {
        return
      }
      
      this.sendPausedIndicator()
    },
    sendTypingIndicator() {
      if (!this.isTyping) {
        this.isTyping = true
        chatService.sendTypingIndicator()
      }
      
      // Clear existing timers
      if (this.typingTimer) {
        clearTimeout(this.typingTimer)
      }
      if (this.pausedTimer) {
        clearTimeout(this.pausedTimer)
      }
      
      // Set timer to send paused indicator if user stops typing
      this.pausedTimer = setTimeout(() => {
        this.sendPausedIndicator()
      }, 1000*10) // 10 seconds of inactivity
    },
    sendPausedIndicator() {
      if (this.isTyping) {
        this.isTyping = false
        chatService.sendPausedIndicator()
      }
      
      // Clear timers
      if (this.typingTimer) {
        clearTimeout(this.typingTimer)
        this.typingTimer = null
      }
      if (this.pausedTimer) {
        clearTimeout(this.pausedTimer)
        this.pausedTimer = null
      }
    },
  },
  computed: {
    hasBotUploading() {
      return this.botPendingAttachments.some(a => a.uploadStatus === 'uploading')
    },
    expandedBox() {
      let lines = this.newMessageText.split(/\r|\r\n|\n/)

      if (this.newMessageText.length > 84) {
        return true
      } else if (lines.length == 2 && lines[1].length > 42) {
        return true
      } else if (lines.length >= 3) {
        return true
      } else {
        return false
      }
    },
    disableCTAs() {
      if (this.chatStore.state.chatSource == 'bot') {
        return (
          (!this.newMessageText && this.botPendingAttachments.length === 0) ||
          this.submittingMessage ||
          this.hasBotUploading
        )
      }
      return (
        (!this.newMessageText && !this.newFiles.length) ||
        this.submittingFileTransfer ||
        this.submittingMessage
      )
    },
  },
  async mounted() {
    var pasteFileHandler = new PasteFileHandler(
      document.getElementById('message-input'),
    )
    pasteFileHandler.on('paste-file', imageFile => {
      const newFile = { file: imageFile, uploadPercent: 5 }
      this.newFiles.push(newFile)
      this.uploadFile(newFile)
    })
    let messageInput = document.getElementById('message-input')
    messageInput && messageInput.focus()

    if (this.chatStore.state.botChatImage) {
      let response = await uploadFile(
        chatService.commData,
        base64ToFile(
          this.chatStore.state.botChatImage,
          'auto-generated-bot-chat-cxm.png',
        ),
        '',
      )
      if (response.results && response.results[0]) {
        chatService.sendMessage(
          JSON.stringify({ ...response.results[0], hideMessage: true }),
        )
      }
      this.chatStore.storeBotChatImage(null)
    }
  },
  unmounted() {
    // Clean up timers
    if (this.typingTimer) {
      clearTimeout(this.typingTimer)
    }
    if (this.pausedTimer) {
      clearTimeout(this.pausedTimer)
    }
    // Clean up any remaining blob URLs (persistent URLs must not be revoked)
    this.botPendingAttachments.forEach(att => {
      if (att.previewUrl && att.previewUrl.startsWith('blob:')) URL.revokeObjectURL(att.previewUrl)
    })
  },
}
</script>

<style scoped lang="scss">
.message-box {
  border-top: 1px solid #dadbe0;
  position: absolute;
  height: 60px;
  bottom: 0px;
  left: 0px;
  right: 0px;
  background: white;

  transition: 300ms ease height;
  .expanded-box & {
    height: 108px;
  }
  overflow: hidden;
}

.message-input {
  border: none;
  resize: none;
  padding-top: 12px;
  margin-top: 2px;
  color: #303030;
  // width: 79%;
  // font-size: 14px;
  font-size: 16px;
  transform: scale(0.875);
  transform-origin: left;
  width: 90%;

  &::placeholder {
    color: #6b737b;
  }

  &:disabled {
    background: inherit;
    cursor: not-allowed;

    &::placeholder {
      color: transparent;
    }
  }
}

.file-input {
  display: none;
}

.message-button {
  background: transparent;
  border: none;
  font-size: 20px;
  margin-top: 14px;
  vertical-align: top;
  display: inline-block;
  color: #898d94;
  line-height: 14px;
  padding: 3px 5px;

  &:focus-visible {
    outline: double;
    outline-color: #898d94;
  }
}

.message-attachment-button:hover {
  color: #939292;
}

.message-send-button {
  margin-right: 2px;
}
.message-send-button-disabled {
  color: #898d94;
  cursor: not-allowed;
}

.bt-icon-send-filled {
  color: v-bind(sendButtonColor);

  &:hover {
    color: v-bind(sendButtonHoverColor);
  }
}

.files {
  right: 24px;
  left: 24px;
  z-index: 1;
  position: absolute;
  bottom: 60px;

  transition: 300ms ease bottom;
  .expanded-box & {
    bottom: 108px;
  }
}

.file-upload {
  background: #f8fbfb;
  height: 32px;
  color: #4f4f4f;
  overflow: hidden;
  position: relative;
  margin: 2px 0px;
  border-radius: 6px;
}

.progress-bar {
  width: 100px;
  height: 18px;
  line-height: 18px;
  position: absolute;
  left: 10px;
  top: 7px;
  border-radius: 20px;
  background: white;
  &.upload-complete {
    width: 18px;
  }
  transition: 500ms all linear;
  transition-delay: 750ms;
}

.progress-bar-indicator {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  border-radius: 20px;
  transition: all 250ms linear;
  border: 1px solid white;
}

.file-name {
  font-size: 14px;
  line-height: 32px;
  display: inline-block;
  margin-left: 6px;
  position: absolute;
  left: 114px;
  right: 26px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  &.upload-complete {
    left: 26px;
  }
  transition: 500ms all linear;
  transition-delay: 750ms;
}
.upload-cancel-button {
  border-radius: 6px;
  width: 20px;
  height: 20px;
  color: #4f4f4f;
  font-size: 15px;
  line-height: 24px;
  text-align: center;
  position: absolute;
  right: 6px;
  top: 6px;
  padding-left: 0;
  padding-right: 0;
  background: transparent;

  &:focus {
    outline-style: double;
    outline-color: #4f4f4f;
  }
}

.input-buttons {
  position: absolute;
  top: 0px;
  right: 0px;
}
.disabled {
  cursor: not-allowed;
}

.bt-icon-check-circle {
  color: white;
  opacity: 0;
  &.upload-complete {
    opacity: 1;
  }
  transition: 500ms all linear;
  transition-delay: 750ms;
}

// Bot pending image thumbnails + chips
.bot-attachments {
  right: 24px;
  left: 24px;
  z-index: 1;
  position: absolute;
  bottom: 60px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px 0;

  transition: 300ms ease bottom;
  .expanded-box & {
    bottom: 108px;
  }
}

.bot-attachment-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f8fbfb;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 3px 8px;
  font-size: 12px;
  color: #4f4f4f;
  max-width: 180px;
  position: relative;

  &--uploading {
    border-color: #93c5fd;
    background: #eff6ff;
    color: #3b82f6;
  }

  &--error {
    border-color: #fca5a5;
    background: #fef2f2;
    color: #dc2626;
  }

  &--image {
    padding: 0;
    border-radius: 8px;
    overflow: hidden;
    background: #000;
    border-color: #e0e0e0;
  }
}

.bot-attachment-thumbnail-link {
  display: block;
  position: relative;
  line-height: 0;
}

.bot-attachment-thumbnail {
  width: 56px;
  height: 56px;
  object-fit: cover;
  display: block;
  border-radius: 7px;
}

.bot-attachment-thumbnail-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  border-radius: 7px;

  &--error {
    background: rgba(220, 38, 38, 0.6);
  }
}

.bot-attachment-spinner {
  font-size: 11px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.bot-attachment-icon {
  font-size: 11px;
  color: #6b737b;
}

.bot-attachment-icon--error {
  font-size: 11px;
  color: #dc2626;
}

.bot-attachment-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 120px;
}

.bot-attachment-remove {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #6b737b;
  display: flex;
  align-items: center;
  font-size: 11px;
  line-height: 1;

  &:hover {
    color: #4f4f4f;
  }

  &--image {
    position: absolute;
    top: 2px;
    right: 2px;
    background: rgba(0, 0, 0, 0.55);
    color: white;
    border-radius: 50%;
    width: 16px;
    height: 16px;
    font-size: 10px;
    justify-content: center;

    &:hover {
      background: rgba(0, 0, 0, 0.8);
      color: white;
    }
  }
}

// Bot URL input row
.bot-url-input-row {
  right: 24px;
  left: 24px;
  z-index: 1;
  position: absolute;
  bottom: 60px;
  display: flex;
  align-items: center;
  gap: 4px;
  background: white;
  padding: 4px 0;

  transition: 300ms ease bottom;
  .expanded-box & {
    bottom: 108px;
  }

  .bot-attachments + & {
    bottom: auto;
  }
}

.bot-url-input {
  flex: 1;
  border: 1px solid #dadbe0;
  border-radius: 6px;
  padding: 5px 8px;
  font-size: 13px;
  outline: none;

  &:focus {
    border-color: #6b737b;
  }
}

.bot-url-submit,
.bot-url-cancel {
  background: transparent;
  border: 1px solid #dadbe0;
  border-radius: 6px;
  padding: 4px 6px;
  cursor: pointer;
  color: #6b737b;
  font-size: 14px;

  &:hover {
    background: #f8fbfb;
    color: #4f4f4f;
  }
}

.bot-url-btn--active {
  color: v-bind(sendButtonColor);
}
</style>
